import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyMove,
  createInitialBoard,
  formatSquare,
  getLegalMoves,
  getSimpleBotMove,
  getBotMove,
} from '../checkers/engine';
import '../styles/Flappy.css';
import '../styles/Checkers.css';

const DIFFICULTY_LABELS = { 1: '😴 Easy', 2: '🎯 Medium', 3: '🔥 Hard' };
const DIFFICULTY_BOT_NAMES = {
  1: ['Checkie', 'EasyBot', 'NoviceKing'],
  2: ['KingBolt', 'CheckerNova', 'MidnightKing'],
  3: ['GrandMaster', 'KingSlayer', 'IronKing'],
};

const TURN_TIMER_OPTIONS = [10, 15, 20, 30];

function pieceMeta(piece) {
  if (!piece) return null;
  const lower = String(piece).toLowerCase();
  const color = lower === 'r' ? 'red' : 'black';
  const king = piece === 'R' || piece === 'B';
  return { color, king };
}

function formatTimer(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function CheckersPractice() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawLevel = Number(params.get('level') || 2);
  const [difficulty, setDifficulty] = useState(Math.min(3, Math.max(1, rawLevel)));
  const rawBotName = String(params.get('botName') || '').trim();
  const defaultNames = DIFFICULTY_BOT_NAMES[difficulty] || DIFFICULTY_BOT_NAMES[2];
  const botName = rawBotName ? rawBotName.slice(0, 24) : defaultNames[0];
  const [board, setBoard] = useState(() => createInitialBoard());
  const [turn, setTurn] = useState('r');
  const [forcedSquare, setForcedSquare] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [status, setStatus] = useState('Your turn (Red).');
  const [winner, setWinner] = useState(null);
  const [lastMove, setLastMove] = useState(null);
  const [turnTimeoutSeconds, setTurnTimeoutSeconds] = useState(10);
  const [turnStartedAt, setTurnStartedAt] = useState(Date.now());
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 200);
    return () => window.clearInterval(id);
  }, []);

  const timeLeftMs = Math.max(0, (turnStartedAt + turnTimeoutSeconds * 1000) - tick);

  const legalMoves = useMemo(() => {
    if (winner) return [];
    return getLegalMoves(board, turn, forcedSquare);
  }, [board, turn, forcedSquare, winner]);

  const legalTargets = useMemo(() => {
    if (!selectedSquare) return [];
    return legalMoves.filter((m) => m.from === selectedSquare).map((m) => m.to);
  }, [legalMoves, selectedSquare]);

  useEffect(() => {
    if (winner) return;
    if (legalMoves.length > 0) return;

    const win = turn === 'r' ? `${botName} (Black)` : 'You (Red)';
    setWinner(win);
    setStatus(`No legal moves. Winner: ${win}`);
  }, [legalMoves, turn, winner, botName]);

  useEffect(() => {
    if (winner) return;
    if (timeLeftMs > 0) return;

    if (turn === 'r') {
      setWinner(`${botName} (Black)`);
      setStatus(`Turn timeout. ${botName} wins.`);
    } else {
      setWinner('You (Red)');
      setStatus(`${botName} timed out. You win.`);
    }
  }, [timeLeftMs, turn, winner, botName]);

  useEffect(() => {
    if (winner) return;
    if (turn !== 'b') return;
    if (!legalMoves.length) return;

    const timer = window.setTimeout(() => {
      const pick = getBotMove(board, 'b', forcedSquare, difficulty);
      if (!pick) return;

      const result = applyMove(board, 'b', pick.from, pick.to, forcedSquare);
      if (!result.ok) return;

      setBoard(result.board);
      setTurn(result.nextTurn);
      setForcedSquare(result.mustContinueFrom || null);
      setLastMove(result.move);
      setTurnStartedAt(Date.now());

      if (result.nextTurn === 'b') {
        setStatus(`${botName} continues from ${result.mustContinueFrom}.`);
      } else {
        setStatus('Your turn (Red).');
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [board, forcedSquare, legalMoves, turn, winner, botName]);

  const reset = () => {
    setBoard(createInitialBoard());
    setTurn('r');
    setForcedSquare(null);
    setSelectedSquare(null);
    setStatus('Your turn (Red).');
    setWinner(null);
    setLastMove(null);
    setTurnStartedAt(Date.now());
  };

  const onSquareClick = (row, col) => {
    if (winner) return;
    if (turn !== 'r') return;

    const clicked = formatSquare(row, col);

    if (!selectedSquare) {
      const piece = board[row]?.[col];
      if (!piece) return;
      if (String(piece).toLowerCase() !== 'r') return;
      if (!legalMoves.some((m) => m.from === clicked)) return;
      setSelectedSquare(clicked);
      return;
    }

    if (clicked === selectedSquare) {
      setSelectedSquare(null);
      return;
    }

    if (!legalTargets.includes(clicked)) {
      const piece = board[row]?.[col];
      if (piece && String(piece).toLowerCase() === 'r' && legalMoves.some((m) => m.from === clicked)) {
        setSelectedSquare(clicked);
      }
      return;
    }

    const result = applyMove(board, 'r', selectedSquare, clicked, forcedSquare);
    if (!result.ok) {
      setSelectedSquare(null);
      return;
    }

    setBoard(result.board);
    setTurn(result.nextTurn);
    setForcedSquare(result.mustContinueFrom || null);
    setSelectedSquare(null);
    setLastMove(result.move);
    setTurnStartedAt(Date.now());

    if (result.nextTurn === 'r') {
      setStatus(`Continue capture from ${result.mustContinueFrom}.`);
    } else {
      setStatus(`${botName} thinking...`);
    }
  };

  return (
    <div className="chess-match-page">
      {/* ───── TOP BAR ───── */}
      <header className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/checkers/lobby')} title="Lobby">
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Checkers vs {botName}</span>
        </div>
        <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
          <span>⌂</span>
        </button>
      </header>

      {/* ───── TOP PLAYER BAR (Bot / Black) ───── */}
      <div className={`cm-player-bar${turn === 'b' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">⚫</span>
          <span className="cm-player-bar__name">{botName} (Black)</span>
        </div>
        <div className={`cm-clock${turn === 'b' && !winner ? ' cm-clock--ticking' : ''}`}>
          {turn === 'b' && !winner ? formatTimer(timeLeftMs) : `${turnTimeoutSeconds}s`}
        </div>
      </div>

      {/* ───── BOARD ───── */}
      <div className="ck-board-wrap">
        <div className="checkers-board">
          {board.map((row, rIdx) => row.map((piece, cIdx) => {
            const square = formatSquare(rIdx, cIdx);
            const isDark = (rIdx + cIdx) % 2 === 0;
            const isSelected = selectedSquare === square;
            const isTarget = legalTargets.includes(square);
            const isLast = lastMove && (lastMove.from === square || lastMove.to === square);
            const meta = pieceMeta(piece);
            return (
              <button
                key={square}
                type="button"
                className={[
                  'checkers-square',
                  isDark ? 'checkers-square--dark' : 'checkers-square--light',
                  isSelected ? 'checkers-square--selected' : '',
                  isTarget ? 'checkers-square--target' : '',
                  isLast ? 'checkers-square--last' : '',
                ].join(' ')}
                onClick={() => onSquareClick(rIdx, cIdx)}
                disabled={!isDark}
              >
                {meta && (
                  <span className={`checkers-piece checkers-piece--${meta.color}`}>
                    {meta.king ? 'k' : ''}
                  </span>
                )}
              </button>
            );
          }))}
        </div>
      </div>

      {/* ───── BOTTOM PLAYER BAR (You / Red) ───── */}
      <div className={`cm-player-bar${turn === 'r' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🔴</span>
          <span className="cm-player-bar__name">You (Red)</span>
        </div>
        <div className={`cm-clock${turn === 'r' && !winner ? ' cm-clock--ticking' : ''}`}>
          {turn === 'r' && !winner ? formatTimer(timeLeftMs) : `${turnTimeoutSeconds}s`}
        </div>
      </div>

      {/* ───── RESULT BANNER ───── */}
      {winner && (
        <div className={`cm-result-banner${winner.includes('You') ? ' cm-result-banner--win' : ' cm-result-banner--loss'}`}>
          <span className="cm-result-banner__icon">{winner.includes('You') ? '🏆' : '💔'}</span>
          <span>{winner}</span>
        </div>
      )}

      {/* ───── STATUS LINE ───── */}
      <div className="cm-status-line">
        {status}
        {forcedSquare && ` — Must continue from ${forcedSquare}`}
      </div>

      {/* ───── ACTIONS ───── */}
      <div className="cm-actions">
        <button className="cm-btn cm-btn--primary" onClick={reset}>New Game</button>
        <select
          className="cm-btn cm-btn--ghost"
          style={{ appearance: 'auto' }}
          value={difficulty}
          onChange={(e) => { setDifficulty(Number(e.target.value)); reset(); }}
        >
          {[1, 2, 3].map((lvl) => (
            <option key={lvl} value={lvl}>{DIFFICULTY_LABELS[lvl]}</option>
          ))}
        </select>
        <select
          className="cm-btn cm-btn--ghost"
          style={{ appearance: 'auto' }}
          value={turnTimeoutSeconds}
          onChange={(e) => {
            const next = Number(e.target.value);
            setTurnTimeoutSeconds(next);
            setTurnStartedAt(Date.now());
          }}
        >
          {TURN_TIMER_OPTIONS.map((seconds) => (
            <option key={seconds} value={seconds}>Timer: {seconds}s</option>
          ))}
        </select>
      </div>
    </div>
  );
}
